import { Popover, Transition } from '@headlessui/react';
import { DownArrowIcon, PlusIcon } from 'assets/icons';
import classNames from 'classnames';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import MoreFilterPopoverPanel from './MoreFilterPopoverPanel';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';

interface JobMoreFilterProps {
  data: any[];
  dataKey: string;
  selectedDraftArray: string[];
  setSelectedDraftArray: (selectedDraftArray: string[]) => void;
  setSelectedArray: (selectedArray: string[]) => void;
  selectedArray: string[];
  searchText?: string;
  setSearchText?: (searchText: string) => void;
  dataIdKey: string;
  filterPlaceholderText: string;
  filterPlaceholderStyle?: string;
  searchPlaceHolderText?: string;
  searchInputStyle?: string;
  allowSearch: boolean;
  showResultsCount?: boolean;
  secondaryDataKey?: string;
  searchFilterId?: string;
  customClassName?: string;
  filterId?: string;
  amountFilter: AmountFilter;
  setAmountFilter: (amountFilter: AmountFilter) => void;
  externalCompanies: string[];
  setExternalCompanies: (value: string[]) => void;
  externalCompaniesDraft: string[];
  setExternalCompaniesDraft: (value: string[]) => void;
  externalCompanyOptions: { id: string; value: string; label: string }[];
  showJobSourceFilter: boolean;
  createdByData: any[];
  createdBySearchText: string;
  setCreatedBySearchText: (text: string) => void;
  createdByDraft: string[];
  setCreatedByDraft: (arr: string[]) => void;
  createdBySelected: string[];
  setCreatedBySelected: (arr: string[]) => void;
  createdByDataKey?: string;
  createdBySecondaryDataKey?: string;
  createdByDataIdKey?: string;
  leadSourceData: any[];
  leadSourceDraft: string[];
  setLeadSourceDraft: (arr: string[]) => void;
  leadSourceSelected: string[];
  setLeadSourceSelected: (arr: string[]) => void;
  leadSourceDataKey?: string;
  leadSourceDataIdKey?: string;
  serviceAreaOptions: { id: string; name: string }[];
  serviceAreaDraft: string[];
  setServiceAreaDraft: (arr: string[]) => void;
  selectedServiceAreas: string[];
  setSelectedServiceAreas: (arr: string[]) => void;
  serviceAreaSearch: string;
  setServiceAreaSearch: (s: string) => void;
}

const JobMoreFilter: FC<JobMoreFilterProps> = ({
  data,
  dataKey,
  selectedDraftArray,
  setSelectedDraftArray,
  setSelectedArray,
  selectedArray,
  searchText = '',
  setSearchText = () => ({}),
  dataIdKey,
  filterPlaceholderText,
  filterPlaceholderStyle = '',
  searchPlaceHolderText = '',
  searchInputStyle = '',
  allowSearch,
  showResultsCount = true,
  secondaryDataKey = '',
  searchFilterId,
  customClassName,
  filterId,
  amountFilter,
  setAmountFilter,
  externalCompanies,
  setExternalCompanies,
  externalCompaniesDraft,
  setExternalCompaniesDraft,
  externalCompanyOptions,
  showJobSourceFilter,
  createdByData,
  createdBySearchText,
  setCreatedBySearchText,
  createdByDraft,
  setCreatedByDraft,
  createdBySelected,
  setCreatedBySelected,
  createdByDataKey,
  createdBySecondaryDataKey,
  createdByDataIdKey,
  leadSourceData,
  leadSourceDraft,
  setLeadSourceDraft,
  leadSourceSelected,
  setLeadSourceSelected,
  leadSourceDataKey,
  leadSourceDataIdKey,
  serviceAreaOptions,
  serviceAreaDraft,
  setServiceAreaDraft,
  selectedServiceAreas,
  setSelectedServiceAreas,
  serviceAreaSearch,
  setServiceAreaSearch
}) => {
  const { filterOperation, minAmount, maxAmount } = amountFilter ?? {};

  const [operation, setOperation] = useState<AmountFilterOperation>();
  const [startValue, setStartValue] = useState<number>();
  const [endValue, setEndValue] = useState<number>();

  useEffect(() => {
    setOperation(filterOperation);
    setStartValue(minAmount);
    setEndValue(maxAmount);
  }, [filterOperation, minAmount, maxAmount]);

  const getAvailableFilterOptions = () => {
    return [
      {
        label: 'Customer',
        value: 'customer',
        show: selectedArray.length === 0
      },
      {
        label: 'External company',
        value: 'externalCompany',
        show: externalCompanies.length === 0 && showJobSourceFilter
      },
      {
        label: 'Service area',
        value: 'serviceArea',
        show: true
      },
      {
        label: 'Amount',
        value: 'amount',
        show: amountFilter.filterOperation === AmountFilterOperation.ALL
      },
      {
        label: 'Lead source',
        value: 'leadSource',
        show: leadSourceSelected.length === 0 && showJobSourceFilter
      },
      {
        label: 'Created by',
        value: 'createdBy',
        show: createdBySelected.length === 0
      }
    ].filter(option => option.show);
  };

  const availableOptions = getAvailableFilterOptions();
  if (availableOptions.length === 0) return null;

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={twMerge(
              `shrink-0 rounded-md border 
                bg-primary px-3 py-2 text-sbase font-semibold text-primaryText  
                outline-none transition-all  duration-150 ease-linear focus:outline-none 
               border-dashed`,
              classNames({ [customClassName]: customClassName })
            )}
            id={filterId}
            style={{ borderColor: '#ACB5BD' }}
            type="button">
            <div className={'flex shrink-0 flex-row items-center'}>
              <div>
                <PlusIcon />
              </div>
              <div id={`${filterId}-text`} className={'shrink-0 px-3 text-sbase'}>
                More filters
              </div>
              <div>
                <DownArrowIcon className={open ? 'rotate-180' : ''} />
              </div>
            </div>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1">
            <Popover.Panel className="absolute z-40 mt-0.5 min-w-[200px] bg-primary px-4 shadow-md sm:px-0">
              <MoreFilterPopoverPanel
                data={data}
                dataKey={dataKey}
                selectedDraftArray={selectedDraftArray}
                setSelectedDraftArray={setSelectedDraftArray}
                setSelectedArray={setSelectedArray}
                searchText={searchText}
                setSearchText={setSearchText}
                dataIdKey={dataIdKey}
                filterPlaceholderText={filterPlaceholderText}
                filterPlaceholderStyle={filterPlaceholderStyle}
                searchPlaceHolderText={searchPlaceHolderText}
                searchInputStyle={searchInputStyle}
                allowSearch={allowSearch}
                showResultsCount={showResultsCount}
                filterId={filterId}
                secondaryDataKey={secondaryDataKey}
                searchFilterId={searchFilterId}
                close={close}
                operation={operation}
                setOperation={setOperation}
                startValue={startValue}
                setStartValue={setStartValue}
                endValue={endValue}
                setEndValue={setEndValue}
                amountFilter={amountFilter}
                setAmountFilter={setAmountFilter}
                externalCompanies={externalCompanies}
                setExternalCompanies={setExternalCompanies}
                externalCompaniesDraft={externalCompaniesDraft}
                setExternalCompaniesDraft={setExternalCompaniesDraft}
                availableFilterOptions={availableOptions}
                externalCompanyOptions={externalCompanyOptions}
                showJobSourceFilter={showJobSourceFilter}
                createdByData={createdByData}
                createdBySearchText={createdBySearchText}
                setCreatedBySearchText={setCreatedBySearchText}
                createdByDraft={createdByDraft}
                setCreatedByDraft={setCreatedByDraft}
                setCreatedBySelected={setCreatedBySelected}
                createdByDataKey={createdByDataKey}
                createdBySecondaryDataKey={createdBySecondaryDataKey}
                createdByDataIdKey={createdByDataIdKey}
                leadSourceData={leadSourceData}
                leadSourceDraft={leadSourceDraft}
                setLeadSourceDraft={setLeadSourceDraft}
                setLeadSourceSelected={setLeadSourceSelected}
                leadSourceDataKey={leadSourceDataKey}
                leadSourceDataIdKey={leadSourceDataIdKey}
                serviceAreaOptions={serviceAreaOptions}
                serviceAreaDraft={serviceAreaDraft}
                setServiceAreaDraft={setServiceAreaDraft}
                selectedServiceAreas={selectedServiceAreas}
                setSelectedServiceAreas={setSelectedServiceAreas}
                serviceAreaSearch={serviceAreaSearch}
                setServiceAreaSearch={setServiceAreaSearch}
              />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default JobMoreFilter;
